/* =========================================================
   TYGA - HORIZON FLEXIBLE MEGA MENU PROMOS
   Performance optimised version
   ========================================================= */

const SOURCE_SELECTOR = '.tyga-mega-menu-promos-source';
const SOURCE_PROMO_SELECTOR = ':scope > .mega-menu__promo';
const CONFIG_SELECTOR = '[data-mega-menu-promo]';
const MENU_LIST_SELECTOR = '.mega-menu__list[data-mega-menu-key]';
const CLONE_SELECTOR = '[data-tyga-mega-menu-promo-clone]';

const isThemeEditor =
  typeof Shopify !== 'undefined' &&
  Shopify.designMode === true;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getScope(source) {
  return (
    source.closest('.header-section') ||
    source.closest('.shopify-section') ||
    document
  );
}

function setPromoGrid(promo, config, menuList) {
  const columns = clamp(
    Number(menuList.dataset.megaMenuColumns) || 6,
    1,
    8
  );

  let span = clamp(
    Number(config.dataset.megaMenuSpan) || 1,
    1,
    columns
  );

  const startValue =
    config.dataset.megaMenuStart || 'auto';

  if (startValue !== 'auto') {
    const start = clamp(
      Number(startValue) || 1,
      1,
      columns
    );

    span = Math.min(
      span,
      columns - start + 1
    );

    promo.style.gridColumn =
      `${start} / span ${span}`;
  } else {
    promo.style.gridColumn =
      `span ${span}`;
  }
}

function cleanCloneAttributes(clone) {
  clone.removeAttribute('id');
  clone.removeAttribute('data-shopify-editor-block');
  clone.removeAttribute('data-shopify-block-id');
}

function syncSource(source) {
  if (!source || !source.isConnected) return;

  const scope = getScope(source);

  const menuLists =
    Array.from(
      scope.querySelectorAll(MENU_LIST_SELECTOR)
    );

  if (!menuLists.length) return;

  const menuMap = new Map();

  menuLists.forEach((list) => {
    const key = list.dataset.megaMenuKey;

    if (key) {
      menuMap.set(key, list);
    }
  });

  const sourcePromos =
    Array.from(
      source.querySelectorAll(
        SOURCE_PROMO_SELECTOR
      )
    );

  const wantedCloneKeys = new Set();

  sourcePromos.forEach(
    (sourcePromo, index) => {

      const config =
        sourcePromo.querySelector(
          CONFIG_SELECTOR
        );

      if (!config) return;

      const targetKey =
        (
          config.dataset.megaMenuTarget ||
          ''
        ).trim();

      if (!targetKey) return;

      const cloneKey =
        `promo-${index}`;

      wantedCloneKeys.add(cloneKey);

      const targetList =
        menuMap.get(targetKey);

      let clone =
        scope.querySelector(
          `${CLONE_SELECTOR}[data-tyga-mega-menu-promo-clone="${cloneKey}"]`
        );

      if (!targetList) {
        if (clone) clone.remove();
        return;
      }

      /*
       * Create/recreate only when necessary.
       */
      if (
        !clone ||
        clone.parentElement !== targetList
      ) {

        if (clone) {
          clone.remove();
        }

        clone =
          sourcePromo.cloneNode(true);

        clone.setAttribute(
          'data-tyga-mega-menu-promo-clone',
          cloneKey
        );

        cleanCloneAttributes(clone);

        targetList.appendChild(clone);

      } else if (isThemeEditor) {

        /*
         * Only rebuild clone contents while
         * actively editing the theme.
         *
         * On the live storefront the source
         * does not change, so rebuilding it
         * repeatedly is unnecessary and costly.
         */
        if (
          clone.className !==
          sourcePromo.className
        ) {
          clone.className =
            sourcePromo.className;
        }

        clone.innerHTML =
          sourcePromo.innerHTML;

        cleanCloneAttributes(clone);
      }

      const cloneConfig =
        clone.querySelector(
          CONFIG_SELECTOR
        );

      if (cloneConfig) {
        setPromoGrid(
          clone,
          cloneConfig,
          targetList
        );
      }
    }
  );

  /*
   * Remove deleted/stale promo clones.
   */
  scope
    .querySelectorAll(CLONE_SELECTOR)
    .forEach((clone) => {

      const key =
        clone.getAttribute(
          'data-tyga-mega-menu-promo-clone'
        );

      if (!wantedCloneKeys.has(key)) {
        clone.remove();
      }
    });
}

function syncAll() {
  document
    .querySelectorAll(SOURCE_SELECTOR)
    .forEach(syncSource);
}


/* =========================================================
   QUEUED SYNC
   ========================================================= */

let syncQueued = false;

function queueSync() {
  if (syncQueued) return;

  syncQueued = true;

  requestAnimationFrame(() => {
    syncQueued = false;
    syncAll();
  });
}


/* =========================================================
   HEADER OBSERVER
   ========================================================= */

let headerObserver = null;

function mutationNeedsSync(mutation) {
  const target =
    mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

  if (!target) return false;

  /*
   * Ignore our own cloned promo mutations.
   */
  if (
    target.closest &&
    target.closest(CLONE_SELECTOR)
  ) {
    return false;
  }

  if (mutation.type === 'attributes') {
    return [
      'data-mega-menu-target',
      'data-mega-menu-span',
      'data-mega-menu-start',
      'data-mega-menu-key',
      'data-mega-menu-columns'
    ].includes(mutation.attributeName);
  }

  if (mutation.type === 'childList') {

    /*
     * Only care about changes relating to
     * the mega menu or promo source.
     */
    if (
      target.matches?.(SOURCE_SELECTOR) ||
      target.matches?.(MENU_LIST_SELECTOR) ||
      target.closest?.(SOURCE_SELECTOR) ||
      target.closest?.('.header-section')
    ) {
      return true;
    }
  }

  return false;
}

function startHeaderObserver() {
  if (headerObserver) {
    headerObserver.disconnect();
    headerObserver = null;
  }

  const source =
    document.querySelector(
      SOURCE_SELECTOR
    );

  if (!source) return;

  const header =
    source.closest('.header-section') ||
    source.closest('.shopify-section');

  if (!header) return;

  headerObserver =
    new MutationObserver(
      (mutations) => {

        if (
          mutations.some(
            mutationNeedsSync
          )
        ) {
          queueSync();
        }
      }
    );

  headerObserver.observe(
    header,
    {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'data-mega-menu-target',
        'data-mega-menu-span',
        'data-mega-menu-start',
        'data-mega-menu-key',
        'data-mega-menu-columns'
      ]
    }
  );
}


/* =========================================================
   INITIALISATION
   ========================================================= */

function init() {
  syncAll();
  startHeaderObserver();

  /*
   * Horizon may hydrate/replace parts
   * of the header after initial HTML.
   */
  window.addEventListener(
    'load',
    function () {
      queueSync();

      requestAnimationFrame(
        startHeaderObserver
      );
    },
    { once: true }
  );

  /*
   * Theme Editor lifecycle.
   */
  document.addEventListener(
    'shopify:section:load',
    function () {
      queueSync();

      requestAnimationFrame(
        startHeaderObserver
      );
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    queueSync
  );

  document.addEventListener(
    'shopify:block:select',
    queueSync
  );

  document.addEventListener(
    'shopify:block:deselect',
    queueSync
  );
}

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    init,
    { once: true }
  );
} else {
  init();
}